import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  Divider,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Paper,
  Avatar,
  Badge,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  PhotoCamera, 
  Clear, 
  CalendarToday, 
  Send, 
  CheckCircle, 
  Warning, 
  Mic, 
  Loop, 
  Videocam, 
  TextFields,
  Save,
  FolderOpen,
  Edit,
  Delete,
  FileDownload,
  Close,
  Settings,
  DataUsage,
  Update,
  Repeat,
  AlarmOn,
  MoreTime,
  Notifications,
  Refresh,
  Drafts,
  Schedule,
  AddPhotoAlternate,
  AudioFile,
  Send as SendIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

// Composants stylisés
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1F2937' : '#fff',
  borderRadius: '0.75rem',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
}));

const StyledIconWrapper = styled(Box)(({ theme, color }) => ({
  padding: theme.spacing(1.5),
  borderRadius: '0.5rem',
  backgroundColor: alpha(color || theme.palette.primary.main, 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const colors = {
    success: { bg: '#059669', text: '#ECFDF5' },
    warning: { bg: '#D97706', text: '#FFFBEB' },
    error: { bg: '#DC2626', text: '#FEF2F2' },
    default: { bg: '#6B7280', text: '#F3F4F6' },
  };
  
  const color = colors[status] || colors.default;
  
  return {
    backgroundColor: color.bg,
    color: color.text,
    fontWeight: 600,
    '& .MuiChip-label': {
      padding: '0 12px',
    },
  };
});

const ActionButton = styled(Button)(({ theme, variant = 'contained', color = 'primary' }) => ({
  borderRadius: '0.5rem',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  ...(variant === 'contained' && {
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  }),
}));

const PostCreator = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [resettingCooldown, setResettingCooldown] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(new Date());
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [audioFiles, setAudioFiles] = useState([]);
  const [isLoopEnabled, setIsLoopEnabled] = useState(false);
  
  // États pour les brouillons
  const [drafts, setDrafts] = useState([]);
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);

  // États pour la configuration d'auto-post
  const [autoPostConfig, setAutoPostConfig] = useState({
    enabled: false,
    interval: 30,
    lastCheck: null,
    nextScheduledCheck: null,
    maxPerDay: {}  // Objet vide par défaut, sera rempli dynamiquement
  });

  const [autoPostStats, setAutoPostStats] = useState({
    totalDrafts: 0,
    eligibleDrafts: {},  // Objet vide par défaut
    postsToday: {},      // Objet vide par défaut
    nextScheduledCheck: null
  });

  const [autoPostDialogOpen, setAutoPostDialogOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    console.log("PostCreator: Initialisation du composant");
    fetchSessions();
    console.log("PostCreator: Premier appel à fetchScheduledPosts");
    fetchScheduledPosts(); // Charger les posts programmés depuis le serveur
    fetchDrafts(); // Charger les brouillons
    
    // Après un délai, vérifier que les posts ont été chargés
    const initialCheckTimeout = setTimeout(() => {
      console.log("PostCreator: Vérification de l'état après initialisation");
      console.log(`PostCreator: Nombre de posts programmés chargés: ${scheduledPosts.length}`);
      if (scheduledPosts.length === 0) {
        console.log("PostCreator: Aucun post chargé, tentative supplémentaire");
        fetchScheduledPosts(); // Deuxième tentative
      }
    }, 2000);
    
    // Rafraîchir les posts programmés toutes les minutes
    const refreshInterval = setInterval(() => {
      console.log("PostCreator: Appel périodique à fetchScheduledPosts");
      fetchScheduledPosts();
    }, 60000);
    
    // Charger la configuration d'auto-post et les statistiques
    fetchAutoPostConfig();
    fetchAutoPostStats();
    
    // Définir un intervalle pour rafraîchir les statistiques
    const statsInterval = setInterval(() => {
      if (autoPostConfig.enabled) {
        fetchAutoPostStats();
      }
    }, 60000); // Rafraîchir toutes les minutes
    
    return () => {
      clearInterval(refreshInterval);
      clearTimeout(initialCheckTimeout);
      console.log("PostCreator: Nettoyage du composant");
      clearInterval(statsInterval);
    };
  }, []);
  
  // Nettoyage des ressources vidéo quand le composant est démonté
  useEffect(() => {
    return () => {
      // Libérer les URL d'objets pour éviter les fuites de mémoire
      if (video && video.preview) {
        URL.revokeObjectURL(video.preview);
      }
    };
  }, [video]);
  
  // Fonction pour récupérer les posts programmés depuis le serveur
  const fetchScheduledPosts = () => {
    console.log("Récupération des posts programmés...");
    setError(''); // Réinitialiser les erreurs précédentes
    
    axios.get(`${process.env.REACT_APP_API_URL}/api/scheduled-posts`)
      .then(response => {
        console.log("Posts programmés reçus:", response.data);
        if (response.data && Array.isArray(response.data.posts)) {
          const allPosts = response.data.posts;
          const pendingPosts = allPosts.filter(post => !post.published);
          
          console.log(`${allPosts.length} posts programmés au total, dont ${pendingPosts.length} en attente`);
          setScheduledPosts(allPosts);
          
          if (pendingPosts.length > 0) {
            const dailyPosts = pendingPosts.filter(post => post.repeatDaily || post.isLoop).length;
            console.log(`Dont ${dailyPosts} posts quotidiens en attente`);
            if (pendingPosts.length > 0) {
              setSuccess(`${pendingPosts.length} posts programmés en attente` + 
                (dailyPosts > 0 ? `, dont ${dailyPosts} quotidiens` : ''));
            }
          } else if (allPosts.length > 0) {
            setSuccess("Tous les posts programmés ont déjà été publiés");
          }
        } else {
          console.error("Format de réponse inattendu:", response.data);
          setScheduledPosts([]);
          setError("Format de réponse inattendu pour les posts programmés");
        }
      })
      .catch(err => {
        console.error("Erreur lors de la récupération des posts programmés:", err);
        setError(`Erreur de chargement des posts programmés: ${err.message}`);
        // En cas d'erreur, ne pas effacer les posts déjà chargés
      });
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sessions`);
      
      // Process sessions to extract profile information from meta
      const processedSessions = (response.data || []).map(session => {
        // Get profile from meta if it exists
        const profileFromMeta = session.Data && 
                              session.Data.meta && 
                              session.Data.meta.profile;
        
        // Get username from meta if available
        const usernameFromMeta = session.Data && 
                                session.Data.meta && 
                                session.Data.meta.username;
        
        return {
          ...session,
          // Use the profile from meta if available, or the Profile field, or default to "Standard"
          ProfileDisplay: profileFromMeta || session.Profile || "Standard",
          username: usernameFromMeta || "",
        };
      });
      
      setSessions(processedSessions);
      console.log(`${processedSessions.length} sessions chargées`);
    } catch (err) {
      setError('Erreur lors du chargement des sessions');
      console.error('Erreur:', err);
    }
  };

  const handleMediaUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // Détecter le type de fichier
    const file = files[0];
    const isVideo = file.type.startsWith('video/');
    
    // Traiter selon le type de média
    if (isVideo) {
      // Si une vidéo est déjà présente, la supprimer d'abord
      if (video && video.preview) {
        URL.revokeObjectURL(video.preview);
        setVideo(null);
      }
      
      // Supprimer aussi les images si on upload une vidéo
      if (images.length > 0) {
        images.forEach(img => {
          if (img.preview) URL.revokeObjectURL(img.preview);
        });
        setImages([]);
      }
      
      // Créer un nouvel événement avec seulement ce fichier
      const videoEvent = { target: { files: [file] } };
      await handleVideoUpload(videoEvent);
    } else {
      // Si une vidéo est présente et qu'on upload des images, supprimer la vidéo
      if (video && video.preview) {
        URL.revokeObjectURL(video.preview);
        setVideo(null);
      }
      
      // Pour les images, on peut avoir plusieurs fichiers
      await handleImageUpload(event);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    for (const file of files) {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('image', file);
      formData.append('session', selectedSession);

      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/upload`, formData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          },
        });

        setImages(prev => [...prev, {
          id: response.data.uploadId,
          path: response.data.path,
          preview: URL.createObjectURL(file)
        }]);
        
        setSuccess('Image uploadée avec succès');
      } catch (err) {
        setError(err.response?.data?.error || 'Erreur lors de l\'upload de l\'image');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError('');
    setSuccess('');
    
    // Créer un objet URL pour la prévisualisation
    const preview = URL.createObjectURL(file);
    
    try {
      // Extraire les dimensions réelles de la vidéo
      const videoDimensions = await getVideoDimensions(file, preview);
      console.log("Dimensions de la vidéo détectées:", videoDimensions);
      
      // Afficher la prévisualisation
      setVideo({
        file,
        preview,
        name: file.name,
        size: file.size,
        type: file.type,
        uploaded: false,
        uploadId: null,
        width: videoDimensions.width,
        height: videoDimensions.height
      });
      
      // Préparer un timestamp comme ID d'upload
      const timestamp = Date.now().toString();
      
      // Récupérer les informations de session actuelles pour obtenir les en-têtes dynamiques
      console.log("Récupération des informations de session pour les en-têtes dynamiques...");
      const sessionInfoResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sessions/${selectedSession}/headers`
      );
      
      // Récupérer les en-têtes dynamiques depuis la réponse
      const {
        igAppId,
        userAgent
      } = sessionInfoResponse.data;
      
      // Configuration pour la requête d'upload
      const headers = {
        'sec-ch-ua-full-version-list': '"Google Chrome";v="135.0.7049.42", "Not-A.Brand";v="8.0.0.0", "Chromium";v="135.0.7049.42"',
        'sec-ch-ua-platform': 'Windows',
        'Offset': '0',
        'sec-ch-ua': '"Google Chrome";v="135", "Not-A.Brand";v="8", "Chromium";v="135"',
        'sec-ch-ua-model': '""',
        'sec-ch-ua-mobile': '?0',
        'X-IG-APP-ID': igAppId || '238260118697367',
        'Content-Type': 'video/mp4',
        'X-Instagram-Rupload-Params': JSON.stringify({
          "extract_cover_frame": "1",
          "is_sidecar": "0",
          "is_threads": "1",
          "media_type": 2,
          "upload_id": timestamp,
          "upload_media_height": videoDimensions.height, // Utiliser la hauteur réelle de la vidéo
          "upload_media_width": videoDimensions.width // Utiliser la largeur réelle de la vidéo
        }),
        'Referer': 'https://www.threads.net/',
        'X-Entity-Length': file.size.toString(),
        'X-Entity-Type': 'video/mp4',
        'X-Entity-Name': `fb_uploader_${timestamp}`,
        'sec-ch-prefers-color-scheme': 'light',
        'User-Agent': userAgent || 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/135.0.0.0 Safari/537.36',
        'sec-ch-ua-platform-version': '"15.0.0"'
      };
      
      // Créer une requête personnalisée via le serveur proxy
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/upload/video`, 
        file,
        {
          headers: {
            'Content-Type': 'video/mp4',
            'X-Session-Name': selectedSession,
            'X-Upload-ID': timestamp,
            // Envoyer tous les en-têtes nécessaires
            'X-Upload-Headers': JSON.stringify(headers)
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
      
      console.log('Réponse upload vidéo:', uploadResponse.data);
      
      // Si nous avons une réponse réussie
      if (uploadResponse.data.status === 'success') {
        // Mettre à jour l'état de la vidéo
        setVideo(prev => ({
          ...prev,
          uploaded: true,
          uploadId: uploadResponse.data.uploadId,
          path: uploadResponse.data.path,
          width: uploadResponse.data.width || videoDimensions.width,
          height: uploadResponse.data.height || videoDimensions.height
        }));
        
        setSuccess('Vidéo uploadée avec succès');
      } else {
        throw new Error(uploadResponse.data.message || 'Erreur lors de l\'upload de la vidéo');
      }
      
    } catch (err) {
      console.error('Erreur lors de l\'upload de la vidéo:', err);
      setError(err.response?.data?.error || err.message || 'Erreur lors de l\'upload de la vidéo');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Fonction utilitaire pour obtenir les dimensions de la vidéo
  const getVideoDimensions = (file, previewUrl) => {
    return new Promise((resolve, reject) => {
      // Créer un élément vidéo temporaire pour lire les dimensions
      const videoElement = document.createElement('video');
      videoElement.preload = 'metadata';
      
      // Gestionnaire d'événement pour récupérer les dimensions une fois les métadonnées chargées
      videoElement.onloadedmetadata = () => {
        // Récupérer les dimensions réelles
        const width = videoElement.videoWidth;
        const height = videoElement.videoHeight;
        
        // Nettoyer
        videoElement.remove();
        
        // Résoudre avec les dimensions
        resolve({ width, height });
      };
      
      // Gérer les erreurs
      videoElement.onerror = () => {
        // Nettoyer
        videoElement.remove();
        
        // En cas d'erreur, utiliser des dimensions par défaut
        console.warn("Impossible de déterminer les dimensions de la vidéo, utilisation des valeurs par défaut");
        resolve({ width: 720, height: 1280 });
      };
      
      // Démarrer le chargement des métadonnées
      videoElement.src = previewUrl;
    });
  };

  const handleRemoveVideo = () => {
    if (video && video.preview) {
      URL.revokeObjectURL(video.preview);
    }
    setVideo(null);
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleResetCooldown = async () => {
    if (!selectedSession) return;
    
    setResettingCooldown(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sessions/${selectedSession}/reset-cooldown`
      );
      
      setSuccess(response.data.message || 'Cooldown réinitialisé avec succès');
      setLogs([]);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la réinitialisation du cooldown');
      console.error('Erreur:', err);
    } finally {
      setResettingCooldown(false);
    }
  };

  const handleAudioUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files || files.length === 0) return;
    
    for (const file of files) {
      // Verify file is audio
      if (!file.type.startsWith('audio/') && !file.type.includes('mp4')) {
        setError('Veuillez sélectionner un fichier audio MP4 valide');
        continue;
      }
      
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('audio', file);
      formData.append('session', selectedSession);
      formData.append('directory', 'vocaux'); // Specify directory for storage

      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/upload/audio`, formData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          },
        });

        setAudioFiles(prev => [...prev, {
          id: response.data.uploadId,
          path: response.data.path,
          name: file.name,
          preview: URL.createObjectURL(file)
        }]);
        
        setSuccess('Audio uploadé avec succès');
      } catch (err) {
        setError(err.response?.data?.error || 'Erreur lors de l\'upload de l\'audio');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    }
  };

  const handleRemoveAudio = (index) => {
    const newAudioFiles = [...audioFiles];
    URL.revokeObjectURL(newAudioFiles[index].preview);
    newAudioFiles.splice(index, 1);
    setAudioFiles(newAudioFiles);
  };

  const handleResetAllMedia = () => {
    // Clean up video
    if (video && video.preview) {
      URL.revokeObjectURL(video.preview);
      setVideo(null);
    }
    
    // Clean up images
    images.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    setImages([]);
    
    // Clean up audio files
    audioFiles.forEach(audio => {
      if (audio.preview) URL.revokeObjectURL(audio.preview);
    });
    setAudioFiles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Si la programmation est activée, enregistrer le post plutôt que de le publier immédiatement
    if (isScheduleEnabled) {
      handleSchedulePost();
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    setLogs([]);

    try {
      let response;
      
      if (images.length > 0) {
        // Post avec images
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/post/media`, {
          session: selectedSession,
          message: message,
          uploadIds: images.map(img => img.id)
        });
      } else if (audioFiles.length > 0) {
        // Post avec audio
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/post/audio`, {
          session: selectedSession,
          message: message,
          uploadIds: audioFiles.map(audio => audio.id)
        });
      } else if (video && video.uploaded) {
        // Post avec vidéo
        const uploadId = video.uploadId;
        
        // Ajouter un délai aléatoire humain avant de poster
        const minDelay = 1500; // 1.5 secondes minimum
        const maxDelay = 3500; // 3.5 secondes maximum
        const humanDelay = Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;
        
        console.log(`Ajout d'un délai aléatoire humain de ${humanDelay}ms avant de poster la vidéo...`);
        
        // Attendre un délai aléatoire pour simuler un comportement humain
        await new Promise(resolve => setTimeout(resolve, humanDelay));
        
        // Récupérer les informations de session actuelles pour obtenir les en-têtes dynamiques
        console.log("Récupération des informations de session pour les en-têtes dynamiques...");
        const sessionInfoResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/sessions/${selectedSession}/headers`
        );
        
        // Récupérer les en-têtes dynamiques depuis la réponse
        const {
          csrfToken,
          bloksVersionId,
          asbdId,
          igAppId,
          userAgent
        } = sessionInfoResponse.data;
        
        console.log("En-têtes dynamiques récupérés:", {
          csrfToken: csrfToken || "Non disponible, utilisation de la valeur par défaut",
          bloksVersionId: bloksVersionId || "Non disponible, utilisation de la valeur par défaut",
          asbdId: asbdId || "Non disponible, utilisation de la valeur par défaut",
        });
        
        // Préparer les données pour l'API de configuration exactement comme dans l'exemple curl
        const formData = new URLSearchParams();
        formData.append('audience', 'default');
        formData.append('barcelona_source_reply_id', '');
        formData.append('caption', message);
        formData.append('creator_geo_gating_info', '{"whitelist_country_codes":[]}');
        formData.append('custom_accessibility_caption', '');
        formData.append('gen_ai_detection_method', '');
        formData.append('internal_features', '');
        formData.append('is_meta_only_post', '');
        formData.append('is_paid_partnership', '');
        formData.append('is_threads', 'true');
        formData.append('is_upload_type_override_allowed', '1');
        
        // Utiliser exactement le même format que celui capturé dans curl
        const textPostAppInfo = {
          entry_point: "main_tab_bar",
          excluded_inline_media_ids: "[]",
          fediverse_composer_enabled: false,
          gif_media_id: null,
          link_attachment_url: null,
          reply_control: 0,
          special_effects_enabled_str: null,
          tag_header: null,
          text_with_entities: {
            entities: [],
            text: message
          }
        };
        formData.append('text_post_app_info', JSON.stringify(textPostAppInfo));
        formData.append('upload_id', uploadId);
        formData.append('jazoest', '22688');
        
        // Utiliser les en-têtes dynamiques récupérés de la session au lieu des valeurs codées en dur
        const configHeaders = {
          'x-instagram-ajax': '0',
          'sec-ch-ua-platform': 'Windows',
          'x-csrftoken': csrfToken || '4P7UyOKypOZHWsy2m2nlGVbRSJKYb377', // Valeur de secours si non disponible
          'sec-ch-ua-full-version-list': '"Google Chrome";v="135.0.7049.42", "Not-A.Brand";v="8.0.0.0", "Chromium";v="135.0.7049.42"',
          'Referer': 'https://www.threads.net/',
          'sec-ch-ua': '"Google Chrome";v="135", "Not-A.Brand";v="8", "Chromium";v="135"',
          'sec-ch-ua-model': '""',
          'sec-ch-ua-mobile': '?0',
          'x-ig-app-id': igAppId || '238260118697367', // Valeur de secours si non disponible
          'x-asbd-id': asbdId || '359341', // Valeur de secours si non disponible
          'x-bloks-version-id': bloksVersionId || '987ea0d3fd17ef230b1332984bae2dc99f3cb6949af215159037d5d2588f9515', // Valeur de secours
          'sec-ch-prefers-color-scheme': 'light',
          'User-Agent': userAgent || 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/135.0.0.0 Safari/537.36',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'sec-ch-ua-platform-version': '"15.0.0"'
        };
        
        console.log('Données envoyées au format application/x-www-form-urlencoded:', formData.toString());
        
        // Envoyer la requête avec le corps et les headers corrects
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/post/video`, 
          formData.toString(), // Les données au format attendu par Threads.net
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'X-Session-Name': selectedSession,
              'X-Config-Headers': JSON.stringify(configHeaders)
            }
          }
        );
      } else {
        // Post texte uniquement
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/post`, {
          session: selectedSession,
          message: message
        });
      }

      // Récupérer et afficher les logs
      if (response.data.logs && Array.isArray(response.data.logs)) {
        setLogs(response.data.logs);
      }

      setSuccess('Post créé avec succès');
      setMessage('');
      setImages([]);
      setAudioFiles([]);
      if (video && video.preview) {
        URL.revokeObjectURL(video.preview);
      }
      setVideo(null);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création du post');
      // Récupérer les logs même en cas d'erreur
      if (err.response?.data?.logs && Array.isArray(err.response.data.logs)) {
        setLogs(err.response.data.logs);
      }
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Ouvrir la boîte de dialogue pour programmer un post
  const openScheduleDialog = () => {
    setScheduleDialogOpen(true);
  };
  
  // Fermer la boîte de dialogue de programmation
  const closeScheduleDialog = () => {
    setScheduleDialogOpen(false);
  };
  
  // Enregistrer un post programmé
  const handleSchedulePost = () => {
    if (!selectedSession || !message) {
      setError('Veuillez sélectionner une session et saisir un message');
      return;
    }
    
    if (images.length === 0 && !video && audioFiles.length === 0) {
      setError('Veuillez ajouter au moins une image, une vidéo ou un audio');
      return;
    }
    
    setLoading(true);
    
    // Pour les publications quotidiennes, randomiser légèrement l'heure pour éviter la détection
    let finalScheduledDate = new Date(scheduledDate);
    if (isLoopEnabled) {
      // Obtenir une variation aléatoire entre -3 et +3 minutes
      const minuteVariation = Math.floor(Math.random() * 7) - 3;
      // Appliquer la variation
      finalScheduledDate.setMinutes(finalScheduledDate.getMinutes() + minuteVariation);
      
      console.log(`Randomisation appliquée: ${minuteVariation} minutes (heure finale: ${finalScheduledDate.toLocaleTimeString()})`);
    }
    
    const newPost = {
      id: `scheduled-${Date.now()}`,
      session: selectedSession,
      message: message,
      mediaType: video ? 'video' : (audioFiles.length > 0 ? 'audio' : 'image'),
      uploadIds: audioFiles.length > 0 ? audioFiles.map(audio => audio.id) : images.map(img => img.id),
      videoData: video ? {
        uploadId: video.uploadId,
        width: video.width,
        height: video.height,
        name: video.name,
        size: video.size
      } : null,
      scheduledTime: finalScheduledDate.toISOString(),
      createdAt: new Date().toISOString(),
      published: false,
      isLoop: isLoopEnabled,
      lastLoopDate: null,
      repeatDaily: isLoopEnabled
    };

    // Envoi au backend
    axios.post(`${process.env.REACT_APP_API_URL}/api/scheduled-posts`, newPost)
      .then(response => {
        setSuccess('Post programmé avec succès');
        setMessage('');
        setImages([]);
        setAudioFiles([]);
        setVideo(null);
        setIsLoopEnabled(false);
        fetchScheduledPosts();
      })
      .catch(err => {
        setError(err.response?.data?.error || 'Erreur lors de la programmation du post');
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  // Supprimer un post programmé
  const handleDeleteScheduledPost = (postId) => {
    // Supprimer du serveur plutôt que juste localement
    axios.delete(`${process.env.REACT_APP_API_URL}/api/scheduled-posts/${postId}`)
      .then(() => {
        const updatedPosts = scheduledPosts.filter(post => post.id !== postId);
        setScheduledPosts(updatedPosts);
        setSuccess("Post programmé supprimé avec succès");
      })
      .catch(err => {
        setError(`Erreur lors de la suppression: ${err.response?.data?.error || err.message}`);
        console.error("Erreur lors de la suppression:", err);
      });
  };
  
  // Publier immédiatement un post programmé
  const handlePostNow = async (postId) => {
    // Trouver le post
    const postIndex = scheduledPosts.findIndex(post => post.id === postId);
    if (postIndex === -1) return;
    
    const post = scheduledPosts[postIndex];
    const updatedPosts = [...scheduledPosts];
    
    // Mettre à jour le statut du post localement
    updatedPosts[postIndex] = {
      ...post,
      processing: true
    };
    setScheduledPosts(updatedPosts);
    
    // Demander au serveur de publier immédiatement
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/scheduled-posts/${postId}/publish-now`);
      
      if (response.data.logs) {
        setLogs(response.data.logs);
      }
      
      // Mettre à jour la liste des posts programmés
      fetchScheduledPosts();
      
      setSuccess(`Post publié avec succès`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la publication du post');
      
      // Récupérer les logs même en cas d'erreur
      if (err.response?.data?.logs) {
        setLogs(err.response.data.logs);
      }
      
      // Remettre le post en état normal
      const newPosts = [...scheduledPosts];
      if (newPosts[postIndex]) {
        newPosts[postIndex].processing = false;
        setScheduledPosts(newPosts);
      }
      
      console.error('Erreur de publication immédiate:', err);
    }
  };

  // Handler for voice recording (placeholder)
  const handleVoiceRecording = () => {
    // Placeholder for voice recording functionality
    setError('La fonctionnalité d\'enregistrement vocal sera bientôt disponible');
  };

  console.log("Rendu avec", scheduledPosts.length, "posts programmés");
  console.log("Posts non publiés:", scheduledPosts.filter(p => !p.published).length);

  // Fonction pour charger les brouillons
  const fetchDrafts = () => {
    setLoading(true);
    
    axios.get(`${process.env.REACT_APP_API_URL}/api/drafts`)
      .then(response => {
        if (response.data && Array.isArray(response.data.drafts)) {
          setDrafts(response.data.drafts);
          console.log(`${response.data.drafts.length} brouillons chargés`);
        }
      })
      .catch(err => {
        console.error("Erreur lors du chargement des brouillons:", err);
        setError("Erreur lors du chargement des brouillons");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fonction pour sauvegarder le brouillon actuel
  const handleSaveDraft = () => {
    if (!selectedSession) {
      setError('Veuillez sélectionner une session avant de sauvegarder');
      return;
    }
    
    setLoading(true);
    
    // Récupérer le profil associé à la session sélectionnée
    const selectedSessionData = sessions.find(session => session.Name === selectedSession);
    console.log("Session trouvée:", selectedSessionData);
    
    // Déterminer le profil en fonction du nom de la session
    let profileName = "";
    const sessionName = selectedSession.toLowerCase();
    console.log("Nom de session en minuscules:", sessionName);
    
    if (selectedSessionData?.Profile) {
      // Si Profile existe, l'utiliser
      profileName = selectedSessionData.Profile;
      console.log("Profil trouvé dans Profile:", profileName);
    } else if (selectedSessionData?.ProfileDisplay) {
      // Si ProfileDisplay existe, l'utiliser
      profileName = selectedSessionData.ProfileDisplay;
      console.log("Profil trouvé dans ProfileDisplay:", profileName);
    } else {
      // Détection pour Anna
      if (sessionName.includes("anna") || sessionName.includes("an21")) {
        profileName = "Anna";
      } 
      // Détection étendue pour Caro
      else if (
        sessionName.includes("caro") || 
        sessionName.includes("ca21") || 
        sessionName.includes("caroline") ||
        sessionName.startsWith("ca") ||
        sessionName.includes("car")
      ) {
        profileName = "Caro";
      }
      console.log("Profil déterminé par nom de session:", profileName);
    }
    
    // Préparer les données du brouillon
    const draftData = {
      session: selectedSession,
      message: message,
      imageUploadIds: images.map(img => img.id),
      videoData: video ? {
        uploadId: video.uploadId,
        width: video.width,
        height: video.height,
        name: video.name,
        size: video.size
      } : null,
      audioUploadIds: audioFiles.map(audio => audio.id),
      scheduledTime: isScheduleEnabled ? scheduledDate.toISOString() : null,
      isLoopEnabled: isLoopEnabled,
      title: `Brouillon ${new Date().toLocaleString()}`,
      mediaType: video ? 'video' : (audioFiles.length > 0 ? 'audio' : (images.length > 0 ? 'image' : 'text')),
      profile: profileName // Ajout du profil
    };
    
    console.log("Données du brouillon à sauvegarder:", draftData);
    
    // Envoyer au serveur
    axios.post(`${process.env.REACT_APP_API_URL}/api/drafts`, draftData)
      .then(response => {
        const successMessage = `Brouillon sauvegardé avec succès${profileName ? ` (Profil: ${profileName})` : ''}`;
        console.log(successMessage);
        setSuccess(successMessage);
        fetchDrafts(); // Recharger la liste
      })
      .catch(err => {
        console.error("Erreur lors de la sauvegarde:", err);
        setError(`Erreur lors de la sauvegarde: ${err.response?.data?.error || err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fonction pour charger un brouillon
  const handleLoadDraft = (draft) => {
    // Nettoyer l'état actuel
    if (video && video.preview) URL.revokeObjectURL(video.preview);
    images.forEach(img => { if (img.preview) URL.revokeObjectURL(img.preview); });
    audioFiles.forEach(audio => { if (audio.preview) URL.revokeObjectURL(audio.preview); });
    
    // Restaurer les données de base
    setSelectedSession(draft.session || '');
    setMessage(draft.message || '');
    setIsScheduleEnabled(!!draft.scheduledTime);
    setIsLoopEnabled(!!draft.isLoopEnabled);
    
    if (draft.scheduledTime) {
      setScheduledDate(new Date(draft.scheduledTime));
    }
    
    // Déterminer le profil en fonction des données disponibles
    let profileInfo = "";
    
    if (draft.profile) {
      // Si le profil est déjà défini dans le brouillon
      profileInfo = draft.profile;
    } else {
      // Sinon, déterminer le profil en fonction du nom de la session
      const sessionName = draft.session?.toLowerCase() || "";
      console.log("LoadDraft - Nom de session:", sessionName);
      
      // Détection pour Anna
      if (sessionName.includes("anna") || sessionName.includes("an21")) {
        profileInfo = "Anna";
      }
      // Détection étendue pour Caro
      else if (
        sessionName.includes("caro") || 
        sessionName.includes("ca21") || 
        sessionName.includes("caroline") ||
        sessionName.startsWith("ca") ||
        sessionName.includes("car")
      ) {
        profileInfo = "Caro";
      }
    }
    
    console.log("LoadDraft - Profil déterminé:", profileInfo);
    
    // Afficher un message de succès avec le profil si disponible
    if (profileInfo) {
      setSuccess(`Brouillon chargé avec succès (Profil: ${profileInfo})`);
    } else {
      setSuccess('Brouillon chargé avec succès');
    }
    
    // Restaurer les images
    if (draft.imageUploadIds && Array.isArray(draft.imageUploadIds)) {
      const newImages = draft.imageUploadIds.map(id => ({
        id,
        preview: `${process.env.REACT_APP_API_URL}/api/media/preview?type=image&id=${id}`,
        path: `uploads/${id}`
      }));
      setImages(newImages);
    } else {
      setImages([]);
    }
    
    // Restaurer la vidéo
    if (draft.videoData && draft.videoData.uploadId) {
      setVideo({
        uploadId: draft.videoData.uploadId,
        width: draft.videoData.width || 720,
        height: draft.videoData.height || 1280,
        size: draft.videoData.size || 0,
        name: draft.videoData.name || `Video ${draft.videoData.uploadId}`,
        preview: `${process.env.REACT_APP_API_URL}/api/media/preview?type=video&id=${draft.videoData.uploadId}`,
        uploaded: true
      });
    } else {
      setVideo(null);
    }
    
    // Restaurer les audios
    if (draft.audioUploadIds && Array.isArray(draft.audioUploadIds)) {
      const newAudioFiles = draft.audioUploadIds.map(id => ({
        id,
        preview: `${process.env.REACT_APP_API_URL}/api/media/preview?type=audio&id=${id}`,
        path: `vocaux/${id}`,
        name: `Audio ${id}`
      }));
      setAudioFiles(newAudioFiles);
    } else {
      setAudioFiles([]);
    }
    
    setDraftDialogOpen(false);
  };

  // Fonction pour supprimer un brouillon
  const handleDeleteDraft = (id) => {
    axios.delete(`${process.env.REACT_APP_API_URL}/api/drafts/${id}`)
      .then(() => {
        setSuccess('Brouillon supprimé avec succès');
        fetchDrafts(); // Recharger la liste
      })
      .catch(err => {
        setError(`Erreur lors de la suppression: ${err.response?.data?.error || err.message}`);
      });
  };

  // Fonction pour exporter tous les brouillons
  const handleExportAllDrafts = () => {
    setLoading(true);
    
    axios({
      url: `${process.env.REACT_APP_API_URL}/api/drafts/export`,
      method: 'GET',
      responseType: 'blob'
    })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tous_les_brouillons_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setSuccess('Tous les brouillons exportés avec succès');
      })
      .catch(err => {
        setError(`Erreur lors de l'export: ${err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fonction pour charger la configuration d'auto-post
  const fetchAutoPostConfig = () => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/drafts/auto-post/config`)
      .then(response => {
        const config = response.data.config;  // Utiliser response.data.config au lieu de response.data
        // S'assurer que les objets existent avant de les utiliser
        setAutoPostConfig({
          enabled: config.enabled || false,
          interval: config.interval || 30,
          lastCheck: config.lastCheck,
          nextScheduledCheck: config.nextScheduledCheck,
          maxPerDay: config.maxPerDay || {}
        });
      })
      .catch(err => {
        console.error("Erreur lors de la récupération de la config auto-post:", err);
        // En cas d'erreur, garder la config précédente
      });
  };

  // Fonction pour charger les statistiques d'auto-post
  const fetchAutoPostStats = () => {
    if (!autoPostConfig.enabled) return;
    
    axios.get(`${process.env.REACT_APP_API_URL}/api/drafts/auto-post/stats`)
      .then(response => {
        const stats = response.data;
        // S'assurer que les objets existent avant de les utiliser
        setAutoPostStats({
          totalDrafts: stats.totalDrafts || 0,
          eligibleDrafts: stats.eligibleDrafts || {},
          postsToday: stats.postsToday || {},
          nextScheduledCheck: stats.nextScheduledCheck
        });
      })
      .catch(err => {
        console.error("Erreur lors de la récupération des stats auto-post:", err);
        // En cas d'erreur, garder les stats précédentes
      });
  };

  // Fonction pour sauvegarder la configuration d'auto-post
  const saveAutoPostConfig = () => {
    setLoading(true);
    
    axios.post(`${process.env.REACT_APP_API_URL}/api/drafts/auto-post/config`, autoPostConfig)
      .then(() => {
        setSuccess("Configuration d'auto-post sauvegardée avec succès");
        fetchAutoPostStats();
      })
      .catch(err => {
        setError(`Erreur lors de la sauvegarde: ${err.response?.data?.error || err.message}`);
      })
      .finally(() => {
        setLoading(false);
        setAutoPostDialogOpen(false);
      });
  };

  // Fonction pour activer/désactiver l'auto-post pour un brouillon
  const toggleDraftAutoPostEligible = (draftId, eligible) => {
    axios.post(`${process.env.REACT_APP_API_URL}/api/drafts/${draftId}/auto-post`, { eligible })
      .then(() => {
        // Mettre à jour la liste des brouillons localement
        const updatedDrafts = drafts.map(draft => {
          if (draft.id === draftId) {
            return { ...draft, auto_post_eligible: eligible };
          }
          return draft;
        });
        setDrafts(updatedDrafts);
        
        // Mettre à jour les statistiques
        fetchAutoPostStats();
        
        setSuccess(`Brouillon ${eligible ? 'ajouté à' : 'retiré de'} la rotation automatique`);
      })
      .catch(err => {
        setError(`Erreur: ${err.response?.data?.error || err.message}`);
      });
  };

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.mode === 'dark' ? '#111827' : '#f3f4f6',
      minHeight: '100vh',
      p: 3,
    }}>
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: theme.palette.mode === 'dark' ? '#fff' : '#111827'
          }}
        >
          Créer un post
        </Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="Actualiser">
            <IconButton 
              onClick={fetchScheduledPosts}
              sx={{
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.2),
                }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Message de statut */}
      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main,
            '& .MuiAlert-icon': {
              color: theme.palette.success.main,
            },
          }}
        >
          {success}
        </Alert>
      )}

      {/* Formulaire principal */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Sélection de session */}
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel id="session-select-label">Session</InputLabel>
                <Select
                  labelId="session-select-label"
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  label="Session"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.divider, 0.1),
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Sélectionner une session</em>
                  </MenuItem>
                  {sessions.map((session) => (
                    <MenuItem key={session.Name} value={session.Name}>
                      {session.Name} {session.ProfileDisplay ? `(${session.ProfileDisplay})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <ActionButton
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleResetCooldown}
                disabled={resettingCooldown}
                startIcon={<Refresh />}
              >
                {resettingCooldown ? 'Réinitialisation...' : 'Réinitialiser'}
              </ActionButton>
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Écrivez votre message ici..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  },
                }}
              />
            </Grid>

            {/* Boutons d'action média */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <ActionButton
                  variant="contained"
                  color="secondary"
                  startIcon={<AddPhotoAlternate />}
                  component="label"
                  sx={{
                    backgroundColor: '#4F46E5', // indigo-600
                    color: '#FFFFFF', // texte blanc
                    '&:hover': {
                      backgroundColor: '#4338CA', // indigo-500
                      color: '#FFFFFF', // texte blanc au hover aussi
                    }
                  }}
                >
                  Ajouter des médias
                  <input
                    type="file"
                    hidden
                    accept="image/*,video/*"
                    multiple
                    onChange={handleMediaUpload}
                  />
                </ActionButton>
                <ActionButton
                  variant="contained"
                  color="secondary"
                  startIcon={<AudioFile />}
                  component="label"
                  sx={{
                    backgroundColor: '#4F46E5', // indigo-600
                    color: '#FFFFFF', // texte blanc
                    '&:hover': {
                      backgroundColor: '#4338CA', // indigo-500
                      color: '#FFFFFF', // texte blanc au hover aussi
                    }
                  }}
                >
                  Ajouter un vocal
                  <input
                    type="file"
                    hidden
                    accept="audio/*"
                    onChange={handleAudioUpload}
                  />
                </ActionButton>
              </Stack>
            </Grid>

            {/* Programmation */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isScheduleEnabled}
                    onChange={(e) => setIsScheduleEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="Programmer ce post"
              />
            </Grid>

            {/* Boutons d'action principaux */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <ActionButton
                  variant="outlined"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveDraft}
                >
                  Sauvegarder
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  color="warning"
                  startIcon={<Drafts />}
                  onClick={() => setDraftDialogOpen(true)}
                >
                  Brouillons ({drafts.length})
                </ActionButton>
                <ActionButton
                  variant="contained"
                  color="success"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={loading || !selectedSession || !message}
                  sx={{ flex: 1 }}
                >
                  {loading ? 'Publication...' : 'Créer le post'}
                </ActionButton>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* Affichage des logs */}
      {logs.length > 0 && (
        <StyledCard sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Logs
            </Typography>
            <Box sx={{ 
              backgroundColor: alpha(theme.palette.background.paper, 0.5),
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              whiteSpace: 'pre-line',
              maxHeight: '300px',
              overflow: 'auto'
            }}>
              {logs.map((log, index) => (
                <Typography 
                  key={index} 
                  variant="body2" 
                  component="div"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#f0f0f0' : '#2b2b2b',
                    my: 0.5,
                    fontSize: '0.9rem'
                  }}
                >
                  {log}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </StyledCard>
      )}

      {/* Affichage des médias */}
      {(images.length > 0 || video || audioFiles.length > 0) && (
        <StyledCard sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Médias
            </Typography>
            
            {/* Vidéo */}
            {video && (
              <Box sx={{ mb: 3 }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    borderRadius: 2,
                    position: 'relative',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={handleRemoveVideo}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.background.paper, 1),
                      },
                    }}
                  >
                    <Clear />
                  </IconButton>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {video.name}
                  </Typography>
                  <Box sx={{ width: '100%', maxWidth: 480 }}>
                    <video
                      controls
                      src={video.preview}
                      style={{
                        width: '100%',
                        maxHeight: 320,
                        borderRadius: 8,
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mt: 1,
                      color: video.uploaded ? 'success.main' : 'text.secondary',
                    }}
                  >
                    {video.uploaded ? (
                      <>
                        <CheckCircle sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                        <Typography variant="body2">
                          Prêt à publier
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Warning sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                        <Typography variant="body2">
                          En attente d'upload
                        </Typography>
                      </>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Images */}
            {images.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Images ({images.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {images.map((image, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        width: 100,
                        height: 100,
                      }}
                    >
                      <img
                        src={image.preview}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: alpha(theme.palette.background.paper, 0.9),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.background.paper, 1),
                          },
                        }}
                      >
                        <Clear />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Audio */}
            {audioFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Fichiers audio ({audioFiles.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {audioFiles.map((audio, index) => (
                    <Paper
                      key={index}
                      elevation={1}
                      sx={{
                        p: 2,
                        position: 'relative',
                        width: '100%',
                        maxWidth: 320,
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),
                        borderRadius: 2,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveAudio(index)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: alpha(theme.palette.background.paper, 0.9),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.background.paper, 1),
                          },
                        }}
                      >
                        <Clear />
                      </IconButton>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {audio.name}
                      </Typography>
                      <audio
                        controls
                        src={audio.preview}
                        style={{ width: '100%' }}
                      />
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {/* Bouton pour supprimer tous les médias */}
            {(images.length > 0 || video || audioFiles.length > 0) && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <ActionButton
                  variant="outlined"
                  color="error"
                  startIcon={<Clear />}
                  onClick={handleResetAllMedia}
                >
                  Supprimer tous les médias
                </ActionButton>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      )}

      {/* Barre de progression */}
      {uploadProgress > 0 && (
        <Box sx={{ width: '100%', mt: 3 }}>
          <StyledCard>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>
                  Upload en cours...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  },
                }}
              />
            </CardContent>
          </StyledCard>
        </Box>
      )}

      {/* Section des posts programmés */}
      <Box mt={4}>
        <StyledCard>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Posts programmés ({scheduledPosts.filter(p => !p.published).length} en attente)
              </Typography>
              <Stack direction="row" spacing={2}>
                <ActionButton
                  variant="text"
                  color="primary"
                  startIcon={<Refresh />}
                  onClick={fetchScheduledPosts}
                >
                  Actualiser
                </ActionButton>
                <ActionButton
                  variant="text"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {/* handleDeleteAllScheduledPosts */}}
                >
                  Effacer tout
                </ActionButton>
              </Stack>
            </Box>

            {scheduledPosts.length === 0 ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  borderRadius: 1,
                }}
              >
                <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  Aucun post programmé pour le moment. Utilisez le formulaire ci-dessus pour en créer un.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {scheduledPosts
                  .filter(post => !post.published) // Ne montrer que les posts non publiés
                  .sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime)) // Trier par date
                  .map((post) => {
                    // Formater la date correctement
                    const scheduledDate = new Date(post.scheduledTime);
                    const formattedDate = scheduledDate instanceof Date && !isNaN(scheduledDate)
                      ? scheduledDate.toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Date invalide';

                    return (
                      <Grid item xs={12} key={post.id}>
                        <Paper
                          sx={{
                            p: 2,
                            backgroundColor: alpha(theme.palette.background.paper, 0.5),
                            borderRadius: 1,
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {post.session}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {formattedDate}
                                </Typography>
                                {post.isLoop && (
                                  <Chip
                                    size="small"
                                    icon={<Repeat />}
                                    label="Quotidien"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              {post.message && (
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ 
                                    mt: 1,
                                    maxWidth: '600px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                  }}
                                >
                                  {post.message}
                                </Typography>
                              )}
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Publier maintenant">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePostNow(post.id)}
                                  disabled={post.processing}
                                >
                                  {post.processing ? <CircularProgress size={20} /> : <SendIcon />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Modifier">
                                <IconButton size="small">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteScheduledPost(post.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </CardContent>
        </StyledCard>
      </Box>

      {/* Boîte de dialogue pour la programmation */}
      <Dialog 
        open={scheduleDialogOpen} 
        onClose={closeScheduleDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Programmer le post</Typography>
            <IconButton onClick={closeScheduleDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Choisissez la date et l'heure de publication du post (heure de Paris).
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
            <DateTimePicker
              label="Date et heure"
              value={scheduledDate}
              onChange={(newValue) => setScheduledDate(newValue)}
              minDateTime={new Date()}
              format="dd/MM/yyyy HH:mm"
              sx={{ width: '100%' }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  sx: {
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  }
                }
              }}
              ampm={false}
              closeOnSelect={false}
            />
          </LocalizationProvider>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isLoopEnabled}
                  onChange={(e) => setIsLoopEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Répéter tous les jours"
            />
            {isLoopEnabled && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Le post sera publié tous les jours à la même heure, avec une variation aléatoire de ±3 minutes.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <ActionButton onClick={closeScheduleDialog}>
            Annuler
          </ActionButton>
          <ActionButton
            variant="contained"
            onClick={() => {
              closeScheduleDialog();
              handleSchedulePost();
            }}
          >
            Confirmer
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Boîte de dialogue pour les brouillons */}
      <Dialog
        open={draftDialogOpen}
        onClose={() => setDraftDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Gérer les brouillons</Typography>
            <Box display="flex" gap={1}>
              <Tooltip title="Configuration de la rotation automatique">
                <IconButton 
                  color="primary" 
                  onClick={() => setAutoPostDialogOpen(true)}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setDraftDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {drafts.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                borderRadius: 1,
              }}
            >
              <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                Aucun brouillon sauvegardé
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {drafts.map((draft) => (
                <Grid item xs={12} key={draft.id}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.background.paper, 0.5),
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.background.paper, 0.7),
                      }
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {draft.title || `Brouillon du ${new Date(draft.createdAt).toLocaleString()}`}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1, flexWrap: 'wrap' }}>
                          <StatusChip
                            size="small"
                            label={draft.session}
                            status="default"
                          />
                          
                          {draft.profile && (
                            <StatusChip
                              size="small"
                              label={draft.profile}
                              status={draft.profile === "Anna" ? "success" : "warning"}
                            />
                          )}
                          
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              maxWidth: '300px', 
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {draft.message || 'Sans message'}
                          </Typography>
                          
                          {draft.mediaType && (
                            <StatusChip 
                              size="small" 
                              icon={
                                draft.mediaType === 'image' ? <PhotoCamera fontSize="small" /> :
                                draft.mediaType === 'video' ? <Videocam fontSize="small" /> :
                                draft.mediaType === 'audio' ? <Mic fontSize="small" /> :
                                <TextFields fontSize="small" />
                              }
                              label={
                                draft.mediaType === 'image' && draft.imageUploadIds?.length > 1 
                                  ? `${draft.imageUploadIds.length} images`
                                  : draft.mediaType
                              }
                              status="default"
                            />
                          )}
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Inclure dans la rotation automatique">
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={draft.auto_post_eligible === true}
                                onChange={(e) => toggleDraftAutoPostEligible(draft.id, e.target.checked)}
                              />
                            }
                            label=""
                          />
                        </Tooltip>
                        
                        <ActionButton
                          variant="contained"
                          size="small"
                          onClick={() => handleLoadDraft(draft)}
                          startIcon={<EditIcon />}
                        >
                          Modifier
                        </ActionButton>
                        
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteDraft(draft.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <ActionButton 
            onClick={handleExportAllDrafts} 
            startIcon={<DownloadIcon />}
          >
            Exporter tous
          </ActionButton>
          <ActionButton onClick={() => setDraftDialogOpen(false)}>
            Fermer
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Boîte de dialogue pour la configuration d'auto-post */}
      <Dialog
        open={autoPostDialogOpen}
        onClose={() => setAutoPostDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Configuration de la rotation automatique</Typography>
            <IconButton onClick={() => setAutoPostDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <AlarmOn sx={{ mr: 1 }} /> Activation de la rotation automatique
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              La rotation automatique permet de publier automatiquement vos brouillons selon un planning défini.
              Elle sélectionne les brouillons les moins récemment utilisés en respectant les limites quotidiennes.
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={autoPostConfig.enabled}
                  onChange={(e) => setAutoPostConfig({...autoPostConfig, enabled: e.target.checked})}
                />
              }
              label="Activer la rotation automatique"
            />
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <Update sx={{ mr: 1 }} /> Paramètres de fréquence
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Intervalle de vérification (minutes)"
                  type="number"
                  value={autoPostConfig.interval}
                  onChange={(e) => setAutoPostConfig({...autoPostConfig, interval: parseInt(e.target.value)})}
                  InputProps={{ inputProps: { min: 15, max: 1440 } }}
                  fullWidth
                  helperText="Intervalle entre chaque vérification de planification"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Période minimale entre réutilisations (heures)"
                  type="number"
                  value={autoPostConfig.minHoursBetweenUses}
                  onChange={(e) => setAutoPostConfig({...autoPostConfig, minHoursBetweenUses: parseInt(e.target.value)})}
                  InputProps={{ inputProps: { min: 1, max: 720 } }}
                  fullWidth
                  helperText="Temps minimum avant de réutiliser un même brouillon"
                />
              </Grid>
            </Grid>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <Repeat sx={{ mr: 1 }} /> Limites quotidiennes
            </Typography>
            
            <Grid container spacing={2}>
              {Object.entries(autoPostConfig.maxPerDay).map(([profile, value]) => (
                <Grid item xs={12} sm={6} key={profile}>
                  <TextField
                    label={`Posts par jour (${profile})`}
                    type="number"
                    value={value}
                    onChange={(e) => setAutoPostConfig({
                      ...autoPostConfig,
                      maxPerDay: {
                        ...autoPostConfig.maxPerDay,
                        [profile]: parseInt(e.target.value)
                      }
                    })}
                    InputProps={{ inputProps: { min: 0, max: 10 } }}
                    fullWidth
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <DataUsage sx={{ mr: 1 }} /> Statistiques actuelles
            </Typography>
            
            <Grid container spacing={2}>
              {Object.entries(autoPostStats.eligibleDrafts).map(([profile, count]) => (
                <Grid item xs={12} sm={6} key={profile}>
                  <StyledCard>
                    <CardContent>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Compte {profile}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Brouillons éligibles:</Typography>
                        <Typography variant="body2" fontWeight="bold">{count}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Posts aujourd'hui:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {autoPostStats.postsToday[profile] || 0} / {autoPostConfig.maxPerDay[profile]}
                        </Typography>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mt: 1,
                  p: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  borderRadius: 1,
                }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <MoreTime sx={{ mr: 0.5, fontSize: '1rem' }} />
                    Prochaine vérification:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {autoPostStats.nextScheduledCheck 
                      ? new Date(autoPostStats.nextScheduledCheck).toLocaleString() 
                      : 'Non planifiée'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <ActionButton 
            onClick={() => {
              fetchAutoPostConfig();
              fetchAutoPostStats();
            }}
            startIcon={<Refresh />}
          >
            Actualiser
          </ActionButton>
          <ActionButton onClick={() => setAutoPostDialogOpen(false)}>
            Annuler
          </ActionButton>
          <ActionButton 
            onClick={saveAutoPostConfig} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      {error && (
        <Alert
          severity="error"
          sx={{ 
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
            minWidth: 300,
            maxWidth: '90%',
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default PostCreator; 